import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import {
  buildInsights,
  buildPlayStyle,
  officialRanks,
  pctLabel,
  type CommunityView,
  type GuessLike,
  type ScoreBoard,
} from '../lib/communityView';
import { medalTierFromScore } from '../lib/medals';
import { rankAccent } from '../lib/rankColors';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">{title}</div>
      {children}
    </section>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-1 tracking-tight">{value}</div>
      {hint && <div className="text-xs text-foreground/50 mt-0.5">{hint}</div>}
    </div>
  );
}

export function FullStatsPage({
  topicTitle,
  topicPlayDate,
  score,
  guesses,
  community,
  scoreBoard,
  onClose,
}: {
  topicTitle: string;
  topicPlayDate: string;
  score: number;
  guesses: GuessLike[];
  community: CommunityView;
  scoreBoard: ScoreBoard | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const yourRanks = new Set(officialRanks(guesses));
  const yourAvg =
    yourRanks.size > 0
      ? Math.round(Array.from(yourRanks).reduce((a, b) => a + b, 0) / yourRanks.size)
      : null;
  const play = buildPlayStyle(scoreBoard?.realRows ?? []);
  const insights = buildInsights(community, yourRanks);
  const top3 = new Set(community.mostGuessed.slice(0, 3).map((m) => m.rank));
  const overlap = Array.from(yourRanks).filter((r) => top3.has(r)).length;
  const maxDecade = Math.max(1, ...community.decadeShares.map((d) => d.count));

  let formattedDate = topicPlayDate;
  if (topicPlayDate && topicPlayDate.includes('-')) {
    const [y, m, d] = topicPlayDate.split('-');
    formattedDate = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="fixed inset-0 z-[70] bg-background text-foreground overflow-y-auto"
    >
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Back to results"
          >
            <ChevronLeft size={22} />
          </button>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground/50 uppercase tracking-widest">Full stats</div>
            <div className="text-sm font-bold truncate">{topicTitle}</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-16">
        <div className="text-xs font-semibold text-foreground/40 uppercase tracking-widest mb-6">{formattedDate}</div>

        <Section title="You vs the room">
          <div className="grid grid-cols-2 gap-3">
            <Tile label="Your score" value={`${score}`} hint="out of 490" />
            <Tile
              label="Crowd median"
              value={scoreBoard?.median != null ? String(scoreBoard.median) : '—'}
              hint={scoreBoard?.median != null ? (score >= scoreBoard.median ? 'you finished above' : 'you finished below') : undefined}
            />
            <Tile
              label="Better than"
              value={scoreBoard ? `${scoreBoard.percentile}%` : '—'}
              hint="of players today"
            />
            <Tile
              label="Your avg rank"
              value={yourAvg != null ? `#${yourAvg}` : '—'}
              hint={community.crowdAvgRank != null ? `room averaged #${community.crowdAvgRank}` : undefined}
            />
          </div>
          {yourRanks.size > 0 && (
            <p className="mt-3 text-sm text-foreground/60 leading-relaxed">
              {overlap === 0
                ? 'None of your answers were in today’s top 3 most guessed.'
                : `${overlap} of your answers ${overlap === 1 ? 'was' : 'were'} also among today’s most guessed.`}
            </p>
          )}
        </Section>

        {yourRanks.size > 0 && (
          <Section title="Your five, in context">
            <div className="rounded-2xl border border-border bg-muted/40 divide-y divide-border/60">
              {guesses.slice(0, 5).map((g, i) => {
                const ok = g.valid !== false && !g.isHint && g.rank;
                const rank = ok ? Number(g.rank) : null;
                const count = rank ? community.countByRank.get(rank) ?? 0 : 0;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {ok ? (
                          <>
                            <span className="font-mono text-foreground/40 mr-2">#{rank}</span>
                            {g.name ?? 'Unknown'}
                          </>
                        ) : (
                          <span className="text-foreground/45">{g.name || 'Miss'}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-foreground/50 whitespace-nowrap">
                      {ok ? `${pctLabel(count, community.totalPlayers)} also` : 'not on the list'}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        <Section title="How the room scored">
          <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3 space-y-2.5">
            {(scoreBoard?.medalMix ?? []).map((m) => {
              const yours = medalTierFromScore(score) === m.tier;
              return (
                <div key={m.tier} className="flex items-center gap-3">
                  <div className="w-20 shrink-0 text-xs font-bold text-foreground/60">
                    {m.icon} {m.label}
                  </div>
                  <div className="flex-1 h-2 bg-background rounded-full overflow-hidden border border-border">
                    <div
                      className={`h-full rounded-full ${yours ? '' : 'bg-foreground/40'}`}
                      style={{
                        width: `${Math.max(m.pct > 0 ? 4 : 0, Math.min(100, m.pct))}%`,
                        backgroundImage: yours
                          ? 'linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)'
                          : undefined,
                      }}
                    />
                  </div>
                  <div className="w-10 text-right text-xs font-bold tabular-nums text-foreground/50">{m.pct}%</div>
                </div>
              );
            })}
            <div className="flex items-center justify-center gap-1.5 pt-1 text-[10px] font-semibold text-foreground/45">
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundImage: 'linear-gradient(to right, #CD7F32, #a1a1aa, #facc15, #22d3ee)' }}
              />
              You
            </div>
          </div>
        </Section>

        <Section title="The list">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Tile label="Found by someone" value={`${community.foundCount}`} hint="of 100 answers" />
            <Tile label="Still untouched" value={`${community.unguessed}`} hint="not guessed once" />
            <Tile
              label="Centumth"
              value={pctLabel(community.countByRank.get(100) ?? 0, community.totalPlayers)}
              hint="of players found #100"
            />
            <Tile
              label="Crowd avg rank"
              value={community.crowdAvgRank != null ? `#${community.crowdAvgRank}` : '—'}
              hint="where guesses landed"
            />
          </div>

          <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">Guesses by zone</div>
            <div className="space-y-2.5">
              {community.zoneShares.map((z) => (
                <div key={z.id} className="flex items-center gap-3">
                  <div className="w-[7.5rem] shrink-0 text-xs font-bold" style={{ color: z.accent }}>
                    {z.label}
                  </div>
                  <div className="flex-1 h-2 bg-background rounded-full overflow-hidden border border-border">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.max(z.pct > 0 ? 4 : 0, Math.min(100, z.pct))}%`, backgroundColor: z.accent }}
                    />
                  </div>
                  <div className="w-10 text-right text-xs font-bold tabular-nums text-foreground/50">{z.pct}%</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3 mt-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-foreground/40 mb-3">
              By ten-rank band
            </div>
            <div className="flex items-end gap-1 h-16">
              {community.decadeShares.map((d) => (
                <div key={d.lo} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <div
                    className="w-full rounded-sm"
                    style={{
                      height: `${Math.max(6, Math.round((d.count / maxDecade) * 100))}%`,
                      backgroundColor: rankAccent(d.lo + 4),
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1.5 text-[9px] font-bold tracking-widest text-foreground/30 tabular-nums">
              <span>#1–10</span>
              <span>#91–100</span>
            </div>
          </div>
        </Section>

        <Section title="Most guessed">
          <div className="rounded-2xl border border-border bg-muted/40 divide-y divide-border/60">
            {community.mostGuessed.map((m, i) => {
              const yours = yourRanks.has(m.rank);
              return (
                <div key={m.rank} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="w-5 text-xs font-black text-foreground/35 tabular-nums">{i + 1}</div>
                  <div
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: rankAccent(m.rank) }}
                  />
                  <div className="min-w-0 flex-1 text-sm font-semibold truncate">
                    <span className="font-mono text-foreground/40 mr-1.5">#{m.rank}</span>
                    {m.name}
                    {yours && <span className="ml-1.5 text-[10px] font-black uppercase tracking-widest text-foreground/40">You</span>}
                  </div>
                  <div className="text-xs font-bold text-foreground/50">{m.label}</div>
                </div>
              );
            })}
          </div>
        </Section>

        {community.rarestFinds.length > 0 && (
          <Section title="Rarest finds">
            <div className="rounded-2xl border border-border bg-muted/40 divide-y divide-border/60">
              {community.rarestFinds.map((m) => (
                <div key={m.rank} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="text-sm font-semibold min-w-0 truncate">
                    <span className="font-mono text-foreground/40 mr-1.5">#{m.rank}</span>
                    {m.name}
                  </div>
                  <div className="text-xs font-bold text-foreground/50">{m.label}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {community.topMisses.length > 0 && (
          <Section title="Popular misses">
            <div className="rounded-2xl border border-border bg-muted/40 divide-y divide-border/60">
              {community.topMisses.map((m) => (
                <div key={m.text} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="text-sm font-semibold line-through decoration-foreground/30 truncate">{m.text}</div>
                  <div className="text-xs font-bold text-foreground/50 whitespace-nowrap">{m.label}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title="How people opened">
          {play.ready ? (
            <div className="grid grid-cols-3 gap-3">
              <Tile
                label="First guess avg"
                value={play.openAvg != null ? `#${play.openAvg}` : '—'}
                hint={play.openObviousPct != null ? `${play.openObviousPct}% opened obvious` : undefined}
              />
              <Tile label="Last guess avg" value={play.lastAvg != null ? `#${play.lastAvg}` : '—'} hint="the closer" />
              <Tile
                label="Hit rate"
                value={play.hitRate != null ? `${play.hitRate}/5` : '—'}
                hint="guesses that landed"
              />
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border px-4 py-5 text-sm text-foreground/50 leading-relaxed">
              Not enough finished games yet to talk about opening moves without making things up. Check back later.
            </div>
          )}
        </Section>

        {insights.length > 0 && (
          <Section title="Notes from the chart">
            <div className="space-y-2">
              {insights.map((ins) => (
                <div key={ins.key} className="flex items-start gap-2.5 text-sm text-foreground/65 leading-relaxed">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-foreground/30 shrink-0" />
                  <span>{ins.text}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

      </div>
    </motion.div>
  );
}
